import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PhoneIcon = ({ size = 16, color = '#c9935a' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const HangUpIcon = ({ size = 22, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'rotate(135deg)' }}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MicIcon = ({ size = 24, color = '#0e0b0f' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" fill={color} />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
    <line x1="12" y1="19" x2="12" y2="23" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <line x1="8" y1="23" x2="16" y2="23" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const MicOffIcon = ({ size = 24, color = '#e8ddd0' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="3" x2="21" y2="21" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M19 10v2a7 7 0 0 1-.11 1.23M5 10v2a7 7 0 0 0 12 5" />
    <line x1="12" y1="19" x2="12" y2="23" />
  </svg>
);

const GearIcon = ({ size = 15, color = '#c9935a' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// --- voice activity detection tuning ---
// stored per-device in localStorage so you can dial it in on your own phone
const DEFAULT_SILENCE_MS = 2000;   // how long a pause must be before we send
const DEFAULT_SENSITIVITY = 6;     // 1 (needs loud speech) .. 10 (very sensitive)
const MIN_SPEECH_MS = 400;         // ignore coughs, door slams, one-off clicks
// sensitivity 1..10 maps to an RMS threshold; lower threshold = more sensitive
const sensitivityToThreshold = (s) => {
  const clamped = Math.max(1, Math.min(10, Number(s) || DEFAULT_SENSITIVITY));
  return 0.030 - (clamped - 1) * 0.003; // 1 -> 0.030, 10 -> 0.003
};

// Renders whatever media is set for a call — a video loops natively (one
// element, no crossfade, so there's no black flash when it restarts), and an
// image just sits there. For a seamless video loop, make the clip end on the
// same frame it starts on, or append a reversed copy of itself.
function CallMedia({ src, mediaType, style }) {
  if (!src) return null;
  const fill = {
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
    objectFit: 'cover',
  };
  const isImage = mediaType === 'image';
  return (
    <div style={{ ...style, position: 'absolute', overflow: 'hidden' }}>
      {isImage ? (
        <img src={src} alt="" style={fill} />
      ) : (
        // muted + playsInline are mandatory on iOS: unmuted video won't
        // autoplay, and without playsInline Safari forces its own fullscreen
        <video
          src={src}
          style={fill}
          muted
          loop
          autoPlay
          playsInline
          preload="auto"
        />
      )}
    </div>
  );
}

export default function CallPage() {
  const { companionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [companion, setCompanion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');

  // call state
  const [inCall, setInCall] = useState(false);
  const [callStatus, setCallStatus] = useState('idle'); // idle | listening | hearing | thinking | speaking
  const [muted, setMuted] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [level, setLevel] = useState(0); // 0..1 live mic level for the meter

  // settings
  const [showSettings, setShowSettings] = useState(false);
  const [callPrompt, setCallPrompt] = useState('');
  const [defaultCallPrompt, setDefaultCallPrompt] = useState('');
  const [callVoiceId, setCallVoiceId] = useState('');
  const [callModel, setCallModel] = useState('');
  const [callContextLimit, setCallContextLimit] = useState('30');
  // call-only voice sliders — initialised from call values, falling back to chat values
  const [cvStability, setCvStability] = useState(0.5);
  const [cvSimilarity, setCvSimilarity] = useState(0.75);
  const [cvStyle, setCvStyle] = useState(0.0);
  const [cvSpeed, setCvSpeed] = useState(1.0);
  const [callMaxTokens, setCallMaxTokens] = useState(700);
  const [models, setModels] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [silenceMs, setSilenceMs] = useState(
    Number(localStorage.getItem('callSilenceMs')) || DEFAULT_SILENCE_MS
  );
  const [sensitivity, setSensitivity] = useState(
    Number(localStorage.getItem('callSensitivity')) || DEFAULT_SENSITIVITY
  );

  // transcript playback
  const [playingIndex, setPlayingIndex] = useState(null);
  const transcriptAudioRef = useRef(null);

  // --- refs (VAD needs refs, not state — state is stale inside the audio loop) ---
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mimeTypeRef = useRef('audio/webm');
  const isRecordingRef = useRef(false);
  const speechStartRef = useRef(0);
  const lastSpeechRef = useRef(0);
  const busyRef = useRef(false);      // true while thinking/speaking — pause listening
  const mutedRef = useRef(false);
  const inCallRef = useRef(false);
  const statusRef = useRef('idle');
  const silenceMsRef = useRef(silenceMs);
  const thresholdRef = useRef(sensitivityToThreshold(sensitivity));
  const callAudioRef = useRef(null);
  const unlockedAudioElRef = useRef(null);  // pre-unlocked <audio> element for iOS
  const activeSourceRef = useRef(null);     // currently playing WebAudio node
  const timerRef = useRef(null);
  const bottomRef = useRef(null);
  const liveScrollRef = useRef(null);

  useEffect(() => { silenceMsRef.current = silenceMs; }, [silenceMs]);
  useEffect(() => { thresholdRef.current = sensitivityToThreshold(sensitivity); }, [sensitivity]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    fetchCall();
    // iOS suspends the AudioContext when the PWA is backgrounded and doesn't
    // reliably resume it on return — nudge it awake ourselves
    const onVisible = () => {
      if (document.visibilityState === 'visible' && audioCtxRef.current) {
        audioCtxRef.current.resume().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      teardownCall();
    };
  }, [companionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // keep the in-call transcript pinned to the newest line while he's streaming
  useEffect(() => {
    const el = liveScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // setting status from the animation loop would re-render 60x/second, so
  // only push a change when it actually differs
  const setStatus = (next) => {
    if (statusRef.current === next) return;
    statusRef.current = next;
    setCallStatus(next);
  };

  const describeError = (err) => {
    if (err.response) {
      const d = err.response.data?.detail;
      const t = Array.isArray(d)
        ? d.map(x => `${(x.loc || []).join('.')}: ${x.msg}`).join(' | ')
        : (typeof d === 'string' ? d : JSON.stringify(d));
      return `${err.response.status} — ${t || 'no detail'}`;
    }
    return err.message || 'network error';
  };

  const fetchCall = () => {
    setLoading(true);
    axios.get(`${API}/call/${companionId}?token=${token}`)
      .then(res => {
        const c = res.data.companion;
        setCompanion(c);
        setMessages(res.data.messages);
        setDefaultCallPrompt(res.data.default_call_prompt || '');
        // the default goes in as REAL text, not a placeholder — placeholder
        // text can't be edited, which made the prompt look uneditable
        setCallPrompt(c.call_prompt || res.data.default_call_prompt || '');
        setCallVoiceId(c.call_voice_id || '');
        setCallModel(c.call_model || '');
        setCallContextLimit(c.call_context_limit || '30');
        // call sliders start at their saved value, or the chat value if unset
        setCvStability(c.call_voice_stability ?? c.voice_stability ?? 0.5);
        setCvSimilarity(c.call_voice_similarity ?? c.voice_similarity ?? 0.75);
        setCvStyle(c.call_voice_style ?? c.voice_style ?? 0.0);
        setCvSpeed(c.call_voice_speed ?? c.voice_speed ?? 1.0);
        setCallMaxTokens(c.call_max_tokens || 700);
      })
      .catch(err => setErrorMsg(`couldn't load calls: ${describeError(err)}`))
      .finally(() => setLoading(false));
    axios.get(`${API}/models?token=${token}`)
      .then(res => setModels(res.data.models))
      .catch(err => console.log(err));
  };

  // ---- timer ----
  const startTimer = () => {
    setCallSeconds(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setCallSeconds(s => s + 1), 1000);
  };
  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  const formatDuration = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const pickMimeType = () => {
    const candidates = ['audio/webm', 'audio/mp4', 'audio/aac', 'audio/ogg'];
    for (const c of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) {
        return c;
      }
    }
    return '';
  };

  // ---- call lifecycle ----
  // a promise that can't hang the call — resolves null after ms instead
  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise(resolve => setTimeout(() => resolve(null), ms))]);

  const startCall = async () => {
    setErrorMsg('');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorMsg('mic access needs https — check your url');
      return;
    }

    // --- mic first: this is the only thing worth blocking on ---
    let stream;
    try {
      // autoGainControl is what lifts a phone mic's very quiet raw signal to
      // usable levels — with it off, normal speech never rises far enough to
      // register as speech at all. echoCancellation also stops his voice
      // coming back through the speaker and retriggering the mic.
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      } catch (constrainErr) {
        console.log('[call] constrained mic rejected, using plain audio:', constrainErr);
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      streamRef.current = stream;
    } catch (err) {
      console.log('[call] mic error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setErrorMsg('mic access was denied previously — go to Settings → Safari → this site (or aA menu → Website Settings) and allow the microphone, then reload');
      } else if (err.name === 'NotFoundError') {
        setErrorMsg('no microphone found on this device');
      } else {
        setErrorMsg(`mic error: ${err.name || 'unknown'} — ${err.message || ''}`);
      }
      return;
    }

    // --- show the call UI NOW, before any audio setup that might stall.
    // on iOS PWAs, ctx.resume() and the unlock play() can hang until the app
    // is backgrounded and reopened — which was exactly the "overlay only
    // appears after I switch apps" bug.
    inCallRef.current = true;
    busyRef.current = false;
    mutedRef.current = false;
    setMuted(false);
    setInCall(true);
    setStatus('listening');
    startTimer();

    // --- audio setup, none of it allowed to block the call ---
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      if (ctx.state === 'suspended') {
        await withTimeout(ctx.resume(), 1500);
      }

      // silent blip marks WebAudio as user-initiated
      try {
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
      } catch (e) { console.log('[call] blip skipped:', e); }

      // unlock a plain <audio> element too — iOS treats it separately from
      // WebAudio. timeboxed so a hung play() can't stall the call.
      try {
        const el = new Audio();
        el.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
        el.volume = 0;
        await withTimeout(el.play().catch(() => {}), 1000);
        el.pause();
        el.currentTime = 0;
        el.volume = 1;
        unlockedAudioElRef.current = el;
      } catch (e) { console.log('[call] element unlock skipped:', e); }

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      ctx.createMediaStreamSource(stream).connect(analyser);
      analyserRef.current = analyser;
    } catch (err) {
      console.log('[call] audio setup error:', err);
      // the call continues — worst case VAD can't run and we surface that
      setErrorMsg(`audio setup issue: ${err.message || err.name} — try hanging up and calling again`);
    }

    monitorLoop();
  };

  const teardownCall = () => {
    inCallRef.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (mediaRecorderRef.current && isRecordingRef.current) {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    isRecordingRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (activeSourceRef.current) {
      try { activeSourceRef.current.stop(); } catch (e) {}
      activeSourceRef.current = null;
    }
    if (callAudioRef.current) { callAudioRef.current.pause(); callAudioRef.current = null; }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (e) {}
      audioCtxRef.current = null;
    }
    unlockedAudioElRef.current = null;
    stopTimer();
  };

  const endCall = () => {
    teardownCall();
    // clear the listening gate — otherwise hanging up mid-reply leaves it set
    // and the next call would never start recording
    busyRef.current = false;
    isRecordingRef.current = false;
    setInCall(false);
    setStatus('idle');
    setLevel(0);
  };

  // ---- the VAD loop ----
  const monitorLoop = () => {
    const analyser = analyserRef.current;
    if (!analyser || !inCallRef.current) return;
    const data = new Uint8Array(analyser.fftSize);

    const tick = () => {
      if (!inCallRef.current) return;
      analyser.getByteTimeDomainData(data);

      // RMS of the waveform, normalised around the 128 midpoint
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      setLevel(Math.min(1, rms * 12));

      const now = Date.now();
      const speaking = rms > thresholdRef.current;
      // don't listen while he's thinking or talking, or while muted —
      // otherwise his own voice through the speaker retriggers recording
      const canListen = !busyRef.current && !mutedRef.current;

      if (canListen) {
        if (speaking) {
          lastSpeechRef.current = now;
          if (!isRecordingRef.current) {
            speechStartRef.current = now;
            beginUtterance();
          }
        } else if (isRecordingRef.current) {
          const silenceFor = now - lastSpeechRef.current;
          if (silenceFor > silenceMsRef.current) {
            endUtterance();
          }
        }
        // derive the label from what's actually happening rather than setting
        // it once at the start of an utterance — self-correcting, so it can't
        // get stuck showing "listening" while it's really recording you
        if (statusRef.current !== 'thinking' && statusRef.current !== 'replying' && statusRef.current !== 'speaking') {
          setStatus(isRecordingRef.current ? 'hearing' : 'listening');
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const beginUtterance = () => {
    const stream = streamRef.current;
    if (!stream) return;
    try {
      const preferred = pickMimeType();
      const rec = preferred
        ? new MediaRecorder(stream, { mimeType: preferred })
        : new MediaRecorder(stream);
      mimeTypeRef.current = rec.mimeType || preferred || 'audio/webm';
      mediaRecorderRef.current = rec;
      audioChunksRef.current = [];

      rec.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        const spokeFor = Date.now() - speechStartRef.current;
        const blob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current });
        // too short to be real speech, or empty — ignore and keep listening
        if (spokeFor < MIN_SPEECH_MS || blob.size < 1200) {
          busyRef.current = false;
          setStatus('listening');
          return;
        }
        await sendTurn(blob);
      };

      rec.start();
      isRecordingRef.current = true;
      setStatus('hearing');
    } catch (err) {
      console.log('recorder error:', err);
    }
  };

  const endUtterance = () => {
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;
    busyRef.current = true;          // stop listening until his reply finishes
    setStatus('thinking');
    try { mediaRecorderRef.current.stop(); } catch (e) { busyRef.current = false; }
  };

  // Playing his reply on iOS is the fiddly part: audio has to trace back to the
  // tap that started the call. The AudioContext was unlocked then, so decoding
  // into it is the reliable path; the pre-unlocked <audio> element is a fallback.
  // while he's talking, hard-disable the mic track. pausing the VAD stops us
  // *listening*, but the track staying live keeps iOS in record mode, which
  // colours the output — this takes it out of the path entirely.
  const setMicActive = (active) => {
    const stream = streamRef.current;
    if (!stream) return;
    stream.getAudioTracks().forEach(t => { t.enabled = active; });
  };

  const playReply = async (url) => {
    let settled = false;
    setMicActive(false);
    const resume = () => {
      if (settled) return;
      settled = true;
      activeSourceRef.current = null;
      callAudioRef.current = null;
      setMicActive(true);
      busyRef.current = false;
      lastSpeechRef.current = Date.now(); // don't count his voice as your speech
      if (inCallRef.current) setStatus('listening');
    };

    // element playback first: it plays progressively as bytes arrive, which is
    // the whole point of streaming. WebAudio needs the complete file before it
    // can decode, so it only serves as a fallback here.
    try {
      const el = unlockedAudioElRef.current || new Audio();
      // fully reset the element — it's reused every turn, and leaving a
      // half-buffered previous stream attached can corrupt the next one
      try {
        el.pause();
        el.removeAttribute('src');
        el.load();
      } catch (e) {}
      el.src = url;
      el.onended = resume;
      el.onerror = () => {
        // only fall back if nothing played at all. re-fetching the speak URL
        // after a partial play would bill a second ElevenLabs generation.
        const playedSomething = (el.currentTime || 0) > 0.1;
        if (playedSomething) {
          console.log('playback ended early after partial audio — not refetching');
          resume();
        } else {
          console.log('streamed element playback failed, trying webaudio');
          fallbackWebAudio(url, resume);
        }
      };
      callAudioRef.current = el;
      setStatus('speaking');
      await el.play();
      return;
    } catch (e) {
      console.log('element play() rejected, trying webaudio:', e);
    }

    fallbackWebAudio(url, resume);
  };

  const fallbackWebAudio = async (url, resume) => {
    const ctx = audioCtxRef.current;
    if (!ctx) {
      setErrorMsg('audio blocked by the browser — check the silent switch, then call again');
      resume();
      return;
    }
    try {
      if (ctx.state === 'suspended') await withTimeout(ctx.resume(), 1000);
      const res = await fetch(url);
      const arrayBuf = await res.arrayBuffer();
      const decoded = await withTimeout(new Promise((resolve, reject) => {
        const maybe = ctx.decodeAudioData(arrayBuf, resolve, reject);
        if (maybe && typeof maybe.then === 'function') maybe.then(resolve, reject);
      }), 5000);
      if (!decoded) { resume(); return; }
      const src = ctx.createBufferSource();
      src.buffer = decoded;
      src.connect(ctx.destination);
      src.onended = resume;
      activeSourceRef.current = src;
      setStatus('speaking');
      src.start(0);
    } catch (e) {
      console.log('webaudio fallback failed:', e);
      resume();
    }
  };

  // ---- one call turn ----
  // the reply streams back as text while it generates, then we request the
  // audio separately so playback can start before the clip finishes rendering
  const sendTurn = async (blob) => {
    const format = (mimeTypeRef.current || 'audio/webm')
      .split(';')[0].split('/')[1] || 'webm';
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const response = await fetch(`${API}/call/turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          companion_id: companionId,
          audio_base64: base64,
          audio_format: format,
        }),
      });
      if (!response.ok) {
        let detail = `server ${response.status}`;
        try {
          const j = await response.json();
          if (j?.detail) detail = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
        } catch (e) {}
        throw new Error(detail);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let replyId = null;
      let assistantStarted = false;
      let streamError = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;
          let parsed;
          try { parsed = JSON.parse(data); } catch (e) { continue; }

          if (parsed.user_text) {
            replyId = parsed.message_id || replyId;
            setMessages(prev => [...prev, {
              role: 'user',
              content: parsed.user_text,
              message_id: parsed.user_message_id,
              is_voice: true,
              timestamp: new Date().toISOString(),
            }]);
          }
          if (parsed.delta) {
            if (!assistantStarted) {
              assistantStarted = true;
              setStatus('replying');
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: parsed.delta,
                message_id: replyId,
                is_voice: true,
                timestamp: new Date().toISOString(),
              }]);
            } else {
              setMessages(prev => prev.map((m, i) =>
                i === prev.length - 1 ? { ...m, content: m.content + parsed.delta } : m
              ));
            }
          }
          if (parsed.error) streamError = parsed.error;
          if (parsed.done) replyId = parsed.message_id || replyId;
        }
      }

      if (streamError) {
        setErrorMsg(`turn failed: ${streamError}`);
        busyRef.current = false;
        if (inCallRef.current) setStatus('listening');
        return;
      }

      if (replyId && inCallRef.current) {
        await playReply(`${API}/call/speak/${replyId}?token=${encodeURIComponent(token)}`);
      } else {
        busyRef.current = false;
        if (inCallRef.current) setStatus('listening');
      }
    } catch (err) {
      console.log('call turn error:', err);
      // a failed turn shouldn't kill the call — just say so and keep listening
      setErrorMsg(`turn failed: ${err.message || 'network error'}`);
      busyRef.current = false;
      if (inCallRef.current) setStatus('listening');
    }
  };

  const toggleMute = () => {
    setMuted(prev => {
      const next = !prev;
      mutedRef.current = next;
      if (next && isRecordingRef.current) {
        // muting mid-sentence discards what was captured
        isRecordingRef.current = false;
        try { mediaRecorderRef.current.stop(); } catch (e) {}
        audioChunksRef.current = [];
      }
      setStatus(next ? 'idle' : 'listening');
      return next;
    });
  };

  // ---- transcript playback ----
  const playTranscriptAudio = (url, index) => {
    if (playingIndex === index) {
      transcriptAudioRef.current?.pause();
      transcriptAudioRef.current = null;
      setPlayingIndex(null);
      return;
    }
    transcriptAudioRef.current?.pause();
    const audio = new Audio(url);
    transcriptAudioRef.current = audio;
    setPlayingIndex(index);
    audio.onended = () => { setPlayingIndex(null); transcriptAudioRef.current = null; };
    audio.play().catch(err => { console.log('audio error:', err); setPlayingIndex(null); });
  };

  // ---- settings ----
  const saveSettings = () => {
    setSavingSettings(true);
    setSettingsSaved(false);
    localStorage.setItem('callSilenceMs', String(silenceMs));
    localStorage.setItem('callSensitivity', String(sensitivity));
    // if the text matches the built-in default exactly, store it as "no custom
    // prompt" so future default improvements flow through automatically
    const promptToSave = callPrompt.trim() === defaultCallPrompt.trim() ? '' : callPrompt;
    // safety net in case the field is saved before blur ever fires
    const tokensToSave = Math.max(50, Math.min(4000, Number(callMaxTokens) || 700));
    axios.put(`${API}/companions/${companionId}`, {
      token,
      call_prompt: promptToSave,
      call_voice_id: callVoiceId,
      call_model: callModel,
      call_context_limit: callContextLimit,
      call_voice_stability: cvStability,
      call_voice_similarity: cvSimilarity,
      call_voice_style: cvStyle,
      call_voice_speed: cvSpeed,
      call_max_tokens: tokensToSave,
    })
      .then(() => setSettingsSaved(true))
      .catch(err => setErrorMsg(`couldn't save settings: ${describeError(err)}`))
      .finally(() => setSavingSettings(false));
  };

  // ---- call video ----
  const uploadCallVideo = async (file) => {
    if (!file) return;
    // videos are far bigger than images — fail fast with a clear message
    // rather than letting a huge upload silently stall
    if (file.size > 25 * 1024 * 1024) {
      setErrorMsg('file too large — keep it under 25MB');
      return;
    }
    setUploadingVideo(true);
    setErrorMsg('');
    try {
      const formData = new FormData();
      formData.append('file', file, file.name || 'call.mp4');
      const res = await axios.post(
        `${API}/companions/${companionId}/call-video?token=${encodeURIComponent(token)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setCompanion(prev => ({
        ...prev,
        call_video_url: res.data.call_video_url,
        call_media_type: res.data.call_media_type,
      }));
    } catch (err) {
      setErrorMsg(`upload failed: ${describeError(err)}`);
    }
    setUploadingVideo(false);
  };

  const removeCallVideo = async () => {
    try {
      await axios.delete(`${API}/companions/${companionId}/call-video?token=${encodeURIComponent(token)}`);
      setCompanion(prev => ({ ...prev, call_video_url: null, call_media_type: null }));
    } catch (err) {
      setErrorMsg(`couldn't remove it: ${describeError(err)}`);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts.endsWith('Z') ? ts : ts + 'Z');
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const s = {
    container: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif",
      display: 'flex', flexDirection: 'column',
    },
    header: {
      padding: '20px 24px 16px',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)',
      borderBottom: '1px solid rgba(201,147,90,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      flexShrink: 0, gap: '10px',
    },
    backBtn: {
      width: '36px', height: '36px', borderRadius: '50%',
      border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
      color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0,
    },
    headerCenter: { display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 },
    avatar: {
      width: '38px', height: '38px', borderRadius: '50%',
      background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '16px', color: '#c9935a', fontStyle: 'italic', overflow: 'hidden', flexShrink: 0,
    },
    avatarImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
    headerName: { fontSize: '16px', color: '#e8ddd0', fontWeight: '400' },
    headerSub: { fontSize: '11px', color: '#4a3d30', fontWeight: '300' },
    headerRight: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
    iconBtn: {
      width: '36px', height: '36px', borderRadius: '50%',
      border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    callBtn: {
      padding: '9px 18px', borderRadius: '20px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '13px', cursor: 'pointer', fontWeight: 400,
      display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
    },
    errorBanner: {
      margin: '12px 24px 0', background: 'rgba(180,60,60,0.12)',
      border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px',
      padding: '10px 16px', fontSize: '12px', color: '#e0a0a0', flexShrink: 0,
    },
    transcript: {
      flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: '20px 24px 60px', display: 'flex', flexDirection: 'column', gap: '12px',
    },
    empty: { color: '#4a3d30', fontSize: '14px', textAlign: 'center', marginTop: '60px', lineHeight: 2 },
    msgRow: { display: 'flex', flexDirection: 'column' },
    bubbleHim: {
      maxWidth: '90%', padding: '13px 17px', borderRadius: '20px',
      borderBottomLeftRadius: '6px', background: '#1e1825', color: '#e8ddd0',
      fontSize: '16px', lineHeight: 1.65, border: '1px solid rgba(201,147,90,0.06)',
      fontWeight: 300, alignSelf: 'flex-start',
    },
    bubbleYou: {
      maxWidth: '90%', padding: '13px 17px', borderRadius: '20px',
      borderBottomRightRadius: '6px', background: '#2a1f16', color: '#e8ddd0',
      fontSize: '16px', lineHeight: 1.65, border: '1px solid rgba(201,147,90,0.1)',
      fontWeight: 300, alignSelf: 'flex-end',
    },
    metaRow: {
      display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px',
      fontSize: '10px', color: '#4a3d30',
    },
    playBtn: {
      width: '24px', height: '24px', borderRadius: '50%', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '10px', cursor: 'pointer', display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'linear-gradient(180deg, #150f1a 0%, #0a0709 100%)',
      zIndex: 300, display: 'flex', flexDirection: 'column',
      alignItems: 'stretch', justifyContent: 'flex-start',
      padding: '36px 26px 44px',
    },
    videoLayer: {
      top: 0, left: 0, right: 0, bottom: 0, zIndex: 0,
    },
    // dark gradient so the transcript stays legible over the image or video —
    // heavier at the bottom where the text lives, lighter up top so his face
    // stays visible
    videoScrim: {
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1,
      background: 'linear-gradient(180deg, rgba(10,7,9,.58) 0%, rgba(10,7,9,.24) 26%, rgba(10,7,9,.62) 62%, rgba(10,7,9,.85) 100%)',
      pointerEvents: 'none',
    },
    overlayContent: {
      position: 'relative', zIndex: 2,
      display: 'flex', flexDirection: 'column',
      flex: 1, minHeight: 0, width: '100%',
    },
    // compact header: avatar and identity sit together at the top, freeing the
    // rest of the screen for what he's actually saying
    overlayTop: {
      display: 'flex', alignItems: 'center', gap: '16px',
      flexShrink: 0, marginBottom: '22px',
    },
    topMeta: { display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 },
    avatarRing: (intensity) => ({
      borderRadius: '50%', padding: '5px', flexShrink: 0,
      boxShadow: `0 0 0 ${2 + intensity * 10}px rgba(201,147,90,${0.05 + intensity * 0.18})`,
      transition: 'box-shadow 0.09s linear',
    }),
    bigAvatar: {
      width: '92px', height: '92px', borderRadius: '50%',
      background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '32px', color: '#c9935a', fontStyle: 'italic', overflow: 'hidden',
      boxShadow: '0 0 0 1px rgba(201,147,90,0.2), 0 0 40px rgba(201,147,90,0.12)',
    },
    bigName: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '25px',
      fontStyle: 'italic', color: '#e8ddd0', fontWeight: 300, lineHeight: 1.1,
    },
    timerRow: { display: 'flex', alignItems: 'baseline', gap: '12px' },
    timer: { fontSize: '13px', color: '#4a3d30', letterSpacing: '0.06em' },
    statusText: {
      fontSize: '11px', color: '#c9935a', letterSpacing: '0.12em',
      textTransform: 'uppercase',
    },
    // his current line sits at the middle of the bottom half of the screen.
    // weighted spacers (3 above, 1 below) do this proportionally, so it holds
    // on any screen height instead of drifting with a fixed offset.
    liveTranscript: {
      flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      display: 'flex', flexDirection: 'column',
      paddingRight: '4px', marginBottom: '24px', minHeight: 0,
    },
    liveSpacerTop: { flex: 3, minHeight: 0 },
    liveSpacerBottom: { flex: 1, minHeight: 0 },
    liveHint: {
      fontSize: '14px', color: '#4a3d30', fontWeight: 300,
    },
    liveText: {
      fontSize: '18px', lineHeight: 1.62, color: '#e8ddd0',
      fontWeight: 300, textAlign: 'left', whiteSpace: 'pre-wrap',
    },
    overlayBottom: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: '26px', flexShrink: 0,
    },
    roundBtn: (active) => ({
      width: '58px', height: '58px', borderRadius: '50%',
      border: active ? 'none' : '1px solid rgba(201,147,90,0.25)',
      background: active ? 'rgba(201,147,90,0.18)' : 'transparent',
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }),
    hangUpBtn: {
      width: '68px', height: '68px', borderRadius: '50%', border: 'none',
      background: '#a33a3a', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 18px rgba(160,50,50,0.4)',
    },
    modalOverlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 400, padding: '24px',
    },
    modal: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '20px', padding: '26px', width: '100%', maxWidth: '480px',
      maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflowY: 'auto',
    },
    modalTitle: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '22px',
      fontStyle: 'italic', color: '#e8ddd0', marginBottom: '10px',
    },
    fieldLabel: {
      fontSize: '11px', letterSpacing: '0.1em', color: '#4a3d30',
      textTransform: 'uppercase', marginBottom: '6px', marginTop: '16px',
    },
    input: {
      width: '100%', background: '#120f18', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '10px', padding: '11px 13px', color: '#e8ddd0',
      fontSize: '13px', fontFamily: "'DM Sans', sans-serif",
      boxSizing: 'border-box', outline: 'none',
    },
    textarea: {
      width: '100%', minHeight: '150px', background: '#120f18',
      border: '1px solid rgba(201,147,90,0.15)', borderRadius: '12px',
      padding: '13px', color: '#e8ddd0', fontSize: '13px', lineHeight: 1.7,
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      boxSizing: 'border-box', outline: 'none', resize: 'vertical',
    },
    slider: { width: '100%', accentColor: '#c9935a' },
    videoUploadBtn: {
      width: '100%', padding: '16px', borderRadius: '14px',
      border: '1px dashed rgba(201,147,90,0.25)', background: 'transparent',
      color: '#8a5f35', fontFamily: "'DM Sans', sans-serif", fontSize: '13px',
      cursor: 'pointer', marginTop: '4px',
    },
    videoCard: {
      background: '#120f18', borderRadius: '14px',
      border: '1px solid rgba(201,147,90,0.12)', padding: '12px',
      display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px',
    },
    videoPreview: {
      width: '100%', maxHeight: '240px', objectFit: 'cover',
      borderRadius: '10px', background: '#0e0b0f',
    },
    videoActions: { display: 'flex', gap: '8px' },
    videoReplaceBtn: {
      flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontFamily: "'DM Sans', sans-serif", fontSize: '12px', cursor: 'pointer',
    },
    videoRemoveBtn: {
      flex: 1, padding: '10px', borderRadius: '10px',
      border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
      color: '#4a3d30', fontFamily: "'DM Sans', sans-serif", fontSize: '12px',
      cursor: 'pointer',
    },
    hint: { fontSize: '11px', color: '#4a3d30', lineHeight: 1.6, marginTop: '8px' },
    statusLine: { fontSize: '11px', color: '#c9935a', marginTop: '14px', minHeight: '14px' },
    modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' },
    btnGhost: {
      padding: '9px 16px', borderRadius: '20px', border: 'none',
      background: 'transparent', color: '#4a3d30', fontSize: '13px',
      cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    },
    btnFilled: {
      padding: '9px 18px', borderRadius: '20px', border: 'none',
      background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f',
      fontSize: '13px', cursor: 'pointer', fontWeight: 400,
      fontFamily: "'DM Sans', sans-serif",
    },
  };

  if (loading) return <div style={s.container} />;

  const statusLabel = {
    idle: muted ? 'muted' : '',
    listening: 'listening…',
    hearing: 'hearing you…',
    thinking: 'thinking…',
    replying: 'replying…',
    speaking: 'speaking…',
  }[callStatus];

  // the ring only pulses while actually picking you up
  const ringIntensity = (callStatus === 'listening' || callStatus === 'hearing') && !muted ? level : 0;

  // only his most recent line is shown during the call — the full exchange is
  // in the transcript behind the overlay
  const latestReply = [...messages].reverse().find(m => m.role === 'assistant');

  return (
    <div style={s.container}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />

      {/* HEADER */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={() => navigate(`/threads/${companionId}`)}>←</button>
        <div style={s.headerCenter}>
          <div style={s.avatar}>
            {companion?.avatar_url
              ? <img src={companion.avatar_url} alt="" style={s.avatarImg} />
              : companion?.name?.[0]}
          </div>
          <div>
            <div style={s.headerName}>{companion?.name}</div>
            <div style={s.headerSub}>call history</div>
          </div>
        </div>
        <div style={s.headerRight}>
          <button style={s.iconBtn} onClick={() => setShowSettings(true)} title="call settings">
            <GearIcon size={15} color="#c9935a" />
          </button>
          <button style={s.callBtn} onClick={startCall}>
            <PhoneIcon size={14} color="#0e0b0f" /> Call
          </button>
        </div>
      </div>

      {errorMsg && <div style={s.errorBanner}>⚠ {errorMsg}</div>}

      {/* TRANSCRIPT */}
      <div style={s.transcript}>
        {messages.length === 0 ? (
          <div style={s.empty}>no calls yet 📞<br />tap Call to start one</div>
        ) : (
          messages.map((m, i) => (
            <div key={m.message_id || i} style={s.msgRow}>
              <div style={m.role === 'user' ? s.bubbleYou : s.bubbleHim}>{m.content}</div>
              <div style={{ ...s.metaRow, alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                {m.role === 'assistant' && m.message_id && (
                  <button
                    style={s.playBtn}
                    onClick={() => playTranscriptAudio(
                      // the endpoint redirects to the stored clip once the
                      // background upload lands, so this works for lines that
                      // were just streamed as well as older ones
                      m.audio_url || `${API}/call/speak/${m.message_id}?token=${encodeURIComponent(token)}`,
                      i
                    )}
                  >
                    {playingIndex === i ? '⏸' : '▶'}
                  </button>
                )}
                <span>{formatTimestamp(m.timestamp)}</span>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* CALL OVERLAY */}
      {inCall && (
        <div style={s.overlay}>
          {/* full-bleed looping clip behind everything, with a scrim so the
              transcript stays readable over it */}
          {companion?.call_video_url && (
            <>
              <CallMedia src={companion.call_video_url} mediaType={companion.call_media_type} style={s.videoLayer} />
              <div style={s.videoScrim} />
            </>
          )}

          <div style={s.overlayContent}>
          <div style={s.overlayTop}>
            {!companion?.call_video_url && (
              <div style={s.avatarRing(ringIntensity)}>
                <div style={s.bigAvatar}>
                  {companion?.avatar_url
                    ? <img src={companion.avatar_url} alt="" style={s.avatarImg} />
                    : companion?.name?.[0]}
                </div>
              </div>
            )}
            <div style={s.topMeta}>
              <div style={s.bigName}>{companion?.name}</div>
              <div style={s.timerRow}>
                <span style={s.timer}>{formatDuration(callSeconds)}</span>
                <span style={s.statusText}>{statusLabel}</span>
              </div>
            </div>
          </div>

          {/* just his latest line — the full back-and-forth lives in the
              transcript behind this overlay. the 3:1 spacer ratio parks it at
              roughly three-quarters down, i.e. centred in the bottom half. */}
          <div style={s.liveTranscript} ref={liveScrollRef}>
            <div style={s.liveSpacerTop} />
            {latestReply ? (
              <div style={s.liveText}>{latestReply.content}</div>
            ) : (
              <div style={s.liveHint}>start talking whenever you're ready</div>
            )}
            <div style={s.liveSpacerBottom} />
          </div>

          <div style={s.overlayBottom}>
            <button style={s.roundBtn(muted)} onClick={toggleMute} title={muted ? 'unmute' : 'mute'}>
              {muted
                ? <MicOffIcon size={22} color="#c9935a" />
                : <MicIcon size={22} color="#c9935a" />}
            </button>
            <button style={s.hangUpBtn} onClick={endCall} title="hang up">
              <HangUpIcon size={24} color="#fff" />
            </button>
          </div>
          </div>
        </div>
      )}

      {/* SETTINGS MODAL */}
      {showSettings && (
        <div style={s.modalOverlay} onClick={() => setShowSettings(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Call Settings</div>

            <div style={s.fieldLabel}>pause before he replies — {(silenceMs / 1000).toFixed(1)}s</div>
            <input
              type="range" min="800" max="4000" step="100"
              style={s.slider}
              value={silenceMs}
              onChange={e => { setSilenceMs(Number(e.target.value)); setSettingsSaved(false); }}
            />
            <div style={s.hint}>
              How long you have to stop talking before he answers. Shorter feels snappier
              but may cut you off mid-thought.
            </div>

            <div style={s.fieldLabel}>mic sensitivity — {sensitivity} / 10</div>
            <input
              type="range" min="1" max="10" step="1"
              style={s.slider}
              value={sensitivity}
              onChange={e => { setSensitivity(Number(e.target.value)); setSettingsSaved(false); }}
            />
            <div style={s.hint}>
              Higher picks up quieter speech but may trigger on background noise. If he
              starts replying to nothing, turn this down.
            </div>

            <div style={s.fieldLabel}>call prompt</div>
            <textarea
              style={s.textarea}
              value={callPrompt}
              onChange={e => { setCallPrompt(e.target.value); setSettingsSaved(false); }}
            />
            <div style={s.hint}>
              This sits on top of his normal personality — it only changes how he talks
              on calls. "Reset prompt" below restores the built-in default.
            </div>

            <div style={s.fieldLabel}>call background</div>
            <div style={s.hint}>
              Shown behind the call screen. A video loops continuously — for a
              seamless loop, use a clip that ends on the same frame it starts on,
              or append a reversed copy of itself. A still image works too. Under 25MB.
            </div>
            {companion?.call_video_url ? (
              <div style={s.videoCard}>
                {companion.call_media_type === 'image' ? (
                  <img src={companion.call_video_url} alt="" style={s.videoPreview} />
                ) : (
                  <video
                    src={companion.call_video_url}
                    style={s.videoPreview}
                    muted
                    loop
                    autoPlay
                    playsInline
                  />
                )}
                <div style={s.videoActions}>
                  <button
                    style={s.videoReplaceBtn}
                    onClick={() => document.getElementById('callVideoInput').click()}
                    disabled={uploadingVideo}
                  >
                    {uploadingVideo ? 'uploading…' : 'replace'}
                  </button>
                  <button style={s.videoRemoveBtn} onClick={removeCallVideo}>remove</button>
                </div>
              </div>
            ) : (
              <button
                style={s.videoUploadBtn}
                onClick={() => document.getElementById('callVideoInput').click()}
                disabled={uploadingVideo}
              >
                {uploadingVideo ? 'uploading…' : '🎬 upload video or image'}
              </button>
            )}
            <input
              type="file"
              id="callVideoInput"
              style={{ display: 'none' }}
              accept="video/*,image/*"
              onChange={e => { const f = e.target.files[0]; e.target.value = ''; uploadCallVideo(f); }}
            />

            <div style={s.fieldLabel}>voice — stability: {Number(cvStability).toFixed(2)}</div>
            <input type="range" min="0" max="1" step="0.05" style={s.slider}
              value={cvStability}
              onChange={e => { setCvStability(Number(e.target.value)); setSettingsSaved(false); }} />

            <div style={s.fieldLabel}>voice — similarity: {Number(cvSimilarity).toFixed(2)}</div>
            <input type="range" min="0" max="1" step="0.05" style={s.slider}
              value={cvSimilarity}
              onChange={e => { setCvSimilarity(Number(e.target.value)); setSettingsSaved(false); }} />

            <div style={s.fieldLabel}>voice — style: {Number(cvStyle).toFixed(2)}</div>
            <input type="range" min="0" max="1" step="0.05" style={s.slider}
              value={cvStyle}
              onChange={e => { setCvStyle(Number(e.target.value)); setSettingsSaved(false); }} />

            <div style={s.fieldLabel}>voice — speed: {Number(cvSpeed).toFixed(2)}</div>
            <input type="range" min="0.7" max="1.2" step="0.05" style={s.slider}
              value={cvSpeed}
              onChange={e => { setCvSpeed(Number(e.target.value)); setSettingsSaved(false); }} />
            <div style={s.hint}>
              These only affect his phone voice — your chat voice sliders stay untouched.
            </div>

            <div style={s.fieldLabel}>elevenlabs voice id</div>
            <input
              style={s.input}
              value={callVoiceId}
              onChange={e => { setCallVoiceId(e.target.value); setSettingsSaved(false); }}
              placeholder="leave empty to use his chat voice"
            />

            <div style={s.fieldLabel}>model</div>
            <select
              style={s.input}
              value={callModel}
              onChange={e => { setCallModel(e.target.value); setSettingsSaved(false); }}
            >
              <option value="">— use his chat model —</option>
              {models.map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
            <div style={s.hint}>
              Faster models make calls feel more natural, since every reply has to
              transcribe, generate, and speak before you hear it.
            </div>

            <div style={s.fieldLabel}>context history</div>
            <select
              style={s.input}
              value={callContextLimit}
              onChange={e => { setCallContextLimit(e.target.value); setSettingsSaved(false); }}
            >
              <option value="10">10 messages — fastest</option>
              <option value="20">20 messages</option>
              <option value="30">30 messages — default</option>
              <option value="50">50 messages</option>
              <option value="100">100 messages — most context</option>
            </select>
            <div style={s.hint}>
              How much of your call history he remembers each turn. More context means
              better continuity but slower, pricier replies.
            </div>

            <div style={s.fieldLabel}>reply length limit — {callMaxTokens || 0} tokens</div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              style={s.input}
              value={callMaxTokens}
              // selecting everything on focus means typing REPLACES the old
              // value instead of appending to it — on mobile the cursor
              // otherwise lands at the end and you end up with "502000"
              onFocus={e => e.target.select()}
              onChange={e => {
                const digits = e.target.value.replace(/[^0-9]/g, '');
                setCallMaxTokens(digits === '' ? '' : Number(digits));
                setSettingsSaved(false);
              }}
            />
            <div style={s.hint}>
              Roughly 0.75 words per token — 700 ≈ a paragraph, 2000+ ≈ he can
              properly monologue. Anything under 50 or over 4000 gets adjusted
              to fit when you save.
            </div>

            <div style={s.statusLine}>{settingsSaved ? 'saved ✓' : ''}</div>

            <div style={s.modalActions}>
              <button style={s.btnGhost} onClick={() => { setCallPrompt(defaultCallPrompt); setSettingsSaved(false); }}>
                reset prompt
              </button>
              <button style={s.btnGhost} onClick={() => setShowSettings(false)}>close</button>
              <button style={s.btnFilled} onClick={saveSettings} disabled={savingSettings}>
                {savingSettings ? 'saving…' : 'save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
